import type { RecordingCaptureMetadata } from "./recording-db"
import { recordingDiagnosticEffect, RecordingDiagnostics } from "./recording-diagnostics"
import { RecordingHealth, type CaptureWarning } from "./recording-health"

export class RecordingCapture {
  private health = new RecordingHealth(Date.now())
  private stopped: Promise<void>
  private resolveStopped!: () => void
  private stopping = false
  private ended = false
  private finalMetadata?: RecordingCaptureMetadata
  private timer?: ReturnType<typeof setInterval>
  private audioContext?: AudioContext
  private analyser?: AnalyserNode
  private source?: MediaStreamAudioSourceNode
  private samples = new Float32Array(2048)
  private warning: CaptureWarning = null
  private emittedChunks = 0
  private lastChunkAt = Date.now()
  private lastChunkBytes = 0
  private lastResumeAt = Date.now()
  private diagnostics: RecordingDiagnostics

  constructor(
    readonly recorder: MediaRecorder,
    private options: {
      diagnostics: RecordingDiagnostics
      onChunk: (blob: Blob) => void
      onInterrupted: () => void
      onWarning: (
        warning: CaptureWarning,
        diagnostics?: {
          emittedChunks: number
          peak: number
          audioContextState?: AudioContextState
          trackEnabled?: boolean
          trackMuted?: boolean
          trackReadyState?: MediaStreamTrackState
        }
      ) => void
      releaseCaptureLock: () => void
    }
  ) {
    this.diagnostics = options.diagnostics
    this.stopped = new Promise((resolve) => {
      this.resolveStopped = resolve
    })
    try {
      this.audioContext = new AudioContext()
      this.analyser = this.audioContext.createAnalyser()
      this.analyser.fftSize = this.samples.length
      this.source = this.audioContext.createMediaStreamSource(recorder.stream)
      this.source.connect(this.analyser)
    } catch {
      // Monitoring is advisory; recording must still work when Web Audio cannot initialize.
      this.source?.disconnect()
      void this.audioContext?.close().catch(() => undefined)
      this.audioContext = undefined
      this.analyser = undefined
      this.source = undefined
    }
    recorder.addEventListener("dataavailable", this.onData)
    recorder.addEventListener("stop", this.onStop)
    recorder.addEventListener("error", this.onError)
    recorder.addEventListener("pause", this.onRecorderPause)
    recorder.addEventListener("resume", this.onRecorderResume)
    this.audioContext?.addEventListener("statechange", this.onAudioContextState)
    recorder.stream.getAudioTracks().forEach((track) => {
      track.addEventListener("mute", this.onTrackMute)
      track.addEventListener("unmute", this.onTrackUnmute)
    })
    recorder.stream.getAudioTracks().forEach((track) => track.addEventListener("ended", this.onTrackEnded))
  }

  start() {
    this.health = new RecordingHealth(Date.now())
    try {
      this.recorder.start(10_000)
    } catch (error) {
      this.dispose()
      this.resolveStopped()
      throw error
    }
    recordingDiagnosticEffect(() => this.diagnostics.start())
    this.lastChunkAt = Date.now()
    this.lastResumeAt = this.lastChunkAt
    this.recordDiagnostic("capture_started")
    this.timer = setInterval(this.sample, 500)
    document.addEventListener("visibilitychange", this.onVisibility)
    document.addEventListener("freeze", this.onFreeze)
    document.addEventListener("resume", this.onPageResume)
    window.addEventListener("pagehide", this.onPageHide)
    window.addEventListener("online", this.onNetwork)
    window.addEventListener("offline", this.onNetwork)
    window.addEventListener("pageshow", this.onPageShow)
    window.addEventListener("pointerdown", this.resumeAudioContext)
    window.addEventListener("keydown", this.resumeAudioContext)
    this.resumeAudioContext()
  }

  pause() {
    if (this.recorder.state !== "recording" || this.stopping) return
    this.recordDiagnostic("user_pause")
    this.health.pause(Date.now())
    this.recorder.pause()
  }

  resume() {
    if (this.recorder.state !== "paused" || this.stopping) return
    this.recordDiagnostic("user_resume")
    this.lastResumeAt = Date.now()
    this.health.resume(Date.now())
    this.recorder.resume()
    this.resumeAudioContext()
  }

  async stop(reason?: string): Promise<RecordingCaptureMetadata> {
    if (reason) this.health.interrupt(reason)
    if (!this.stopping) {
      this.stopping = true
      if (this.recorder.state !== "inactive") this.recorder.stop()
    }
    // Even an already-inactive recorder can still have its final dataavailable/stop events queued.
    await this.stopped
    this.finalMetadata ??= this.health.metadata(Date.now())
    return this.finalMetadata
  }

  private onData = (event: BlobEvent) => {
    if (event.data.size === 0) return
    this.health.chunk(Date.now())
    const gap = Date.now() - Math.max(this.lastChunkAt, this.lastResumeAt)
    if (gap > 20_000) this.recordDiagnostic("chunk_delivery_gap", gap)
    this.lastChunkAt = Date.now()
    this.lastChunkBytes = event.data.size
    this.emittedChunks++
    this.options.onChunk(event.data)
    // Reserve the server's extra sequence for the final buffered data.
    if (this.emittedChunks === 1440 && !this.stopping) this.options.onInterrupted()
  }

  private onStop = () => {
    this.recordDiagnostic(this.stopping ? "recorder_stopped" : "recorder_stopped_unexpectedly")
    this.ended = true
    this.dispose()
    this.resolveStopped()
    if (!this.stopping) {
      this.health.interrupt("recorder_stopped")
      this.options.onInterrupted()
    }
  }

  private onError = () => {
    this.recordDiagnostic("recorder_error")
    this.health.interrupt("recorder_error")
    this.options.onInterrupted()
  }

  private onTrackEnded = () => {
    if (this.stopping || this.ended) return
    this.recordDiagnostic("microphone_ended")
    this.health.interrupt("microphone_disconnected")
    this.options.onInterrupted()
  }

  private resumeAudioContext = () => {
    if (this.audioContext?.state === "suspended") {
      void this.audioContext.resume().catch(() => {
        recordingDiagnosticEffect(() => this.options.onWarning("unavailable"))
      })
    }
  }

  private sample = () => {
    if (this.stopping || this.ended) return
    recordingDiagnosticEffect(() =>
      this.diagnostics.tick((gap, monotonicGap) =>
        this.recordDiagnostic("monitoring_gap", gap, undefined, monotonicGap)
      )
    )
    const track = this.recorder.stream.getAudioTracks()[0]
    let peak = 0
    if (this.audioContext?.state === "running" && track?.enabled && !track.muted && track.readyState === "live") {
      this.analyser?.getFloatTimeDomainData(this.samples)
      for (const sample of this.samples) peak = Math.max(peak, Math.abs(sample))
    }
    const signal = this.audioContext?.state !== "running" ? "unavailable" : peak > 0.001 ? "signal" : "silent"
    const warning = this.health.sample(Date.now(), signal)
    if (warning !== this.warning) {
      this.warning = warning
      this.recordDiagnostic(warning ? "warning_" + warning : "warning_cleared", undefined, peak)
      recordingDiagnosticEffect(() =>
        this.options.onWarning(warning, {
          emittedChunks: this.emittedChunks,
          peak,
          audioContextState: this.audioContext?.state,
          trackEnabled: track?.enabled,
          trackMuted: track?.muted,
          trackReadyState: track?.readyState,
        })
      )
    }
    if ((this.health.metadata(Date.now()).duration_ms ?? 0) >= 4 * 60 * 60 * 1000) this.options.onInterrupted()
  }

  private recordDiagnostic(type: string, gap_ms?: number, peak?: number, monotonic_gap_ms?: number) {
    recordingDiagnosticEffect(() => {
      const track = this.recorder.stream.getAudioTracks()[0]
      this.diagnostics.record({
        type,
        peak,
        sequence: this.emittedChunks,
        bytes: this.lastChunkBytes,
        last_chunk_at: new Date(this.lastChunkAt).toISOString(),
        gap_ms,
        monotonic_gap_ms,
        visibility: document.visibilityState,
        recorder_state: this.recorder.state,
        audio_context_state: this.audioContext?.state,
        track_enabled: track?.enabled,
        track_muted: track?.muted,
        track_ready_state: track?.readyState,
        online: navigator.onLine,
      })
    })
  }

  private onVisibility = () => {
    this.recordDiagnostic("visibility_changed")
    this.sample()
  }
  private onFreeze = () => this.recordDiagnostic("page_freeze")
  private onPageResume = () => {
    this.recordDiagnostic("page_resume")
    this.sample()
  }
  private onPageHide = () => this.recordDiagnostic("page_hide")
  private onPageShow = () => {
    this.recordDiagnostic("page_show")
    this.sample()
  }
  private onNetwork = () => this.recordDiagnostic("network_changed")
  private onRecorderPause = () => this.recordDiagnostic("recorder_pause")
  private onRecorderResume = () => this.recordDiagnostic("recorder_resume")
  private onAudioContextState = () => this.recordDiagnostic("audio_context_changed")
  private onTrackMute = () => this.recordDiagnostic("microphone_muted")
  private onTrackUnmute = () => this.recordDiagnostic("microphone_unmuted")

  private dispose() {
    this.options.releaseCaptureLock()
    clearInterval(this.timer)
    document.removeEventListener("visibilitychange", this.onVisibility)
    document.removeEventListener("freeze", this.onFreeze)
    document.removeEventListener("resume", this.onPageResume)
    window.removeEventListener("pagehide", this.onPageHide)
    window.removeEventListener("online", this.onNetwork)
    window.removeEventListener("offline", this.onNetwork)
    window.removeEventListener("pageshow", this.onPageShow)
    window.removeEventListener("pointerdown", this.resumeAudioContext)
    window.removeEventListener("keydown", this.resumeAudioContext)
    this.recorder.removeEventListener("dataavailable", this.onData)
    this.recorder.removeEventListener("stop", this.onStop)
    this.recorder.removeEventListener("error", this.onError)
    this.recorder.removeEventListener("pause", this.onRecorderPause)
    this.recorder.removeEventListener("resume", this.onRecorderResume)
    this.audioContext?.removeEventListener("statechange", this.onAudioContextState)
    this.recorder.stream.getTracks().forEach((track) => {
      track.removeEventListener("ended", this.onTrackEnded)
      track.removeEventListener("mute", this.onTrackMute)
      track.removeEventListener("unmute", this.onTrackUnmute)
      track.stop()
    })
    this.source?.disconnect()
    void this.audioContext?.close().catch(() => undefined)
  }
}
